import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { useSeo } from './head';
import { injectJsonLd, removeJsonLd } from './jsonld';
import { SITE_URL, SITE_NAME, SITE_DESCRIPTION, SITE_LOGO } from './site';

const HOME_SEO = {
  title: 'AllChinaBuy Spreadsheet — Products, Sellers & Smarter Buying Guides',
  description:
    'Explore AllChinaBuy spreadsheet resources, product finds, seller guides, QC information, shipping costs, reviews and practical China shopping guides — all organized in one place.',
  keywords:
    'AllChinaBuy, AllChinaBuy spreadsheet, AllChinaBuy review, China shopping, replica spreadsheet, AllChinaBuy guide, AllChinaBuy shipping, AllChinaBuy QC, AllChinaBuy coupon, AllChinaBuy discount code',
};

export default function SeoManager() {
  const location = useLocation();
  const path = location.pathname;

  const isArticle = path.startsWith('/article/');
  const slug = isArticle ? path.replace('/article/', '') : '';
  const article = isArticle ? articles.find((a) => a.slug === slug) || articles[0] : null;

  useSeo(
    article
      ? {
          title: `${article.title} | ${SITE_NAME}`,
          description: article.description,
          keywords: `AllChinaBuy, ${article.tag}, China shopping guide`,
          canonicalPath: `/article/${article.slug}`,
          image: article.image,
        }
      : {
          title: HOME_SEO.title,
          description: HOME_SEO.description,
          keywords: HOME_SEO.keywords,
          canonicalPath: '/',
        },
  );

  useEffect(() => {
    if (article) {
      injectJsonLd('jsonld-article', {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.description,
        image: article.image,
        mainEntityOfPage: `${SITE_URL}/article/${article.slug}`,
        author: { '@type': 'Organization', name: SITE_NAME },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: { '@type': 'ImageObject', url: SITE_LOGO },
        },
        datePublished: '2026-08-26',
        dateModified: '2026-08-26',
      });
      injectJsonLd('jsonld-faq', {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: article.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    } else {
      injectJsonLd('jsonld-website', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
        description: SITE_DESCRIPTION,
        inLanguage: 'en',
      });
    }

    return () => {
      removeJsonLd('jsonld-article');
      removeJsonLd('jsonld-faq');
      removeJsonLd('jsonld-website');
    };
  }, [article?.slug, path]);

  return null;
}